import { CircularProgress, Tooltip, Typography } from "@mui/material";

import { IconImage, ReportsInfoTooltip } from "@/components";
import { ContainedButton } from "@/common";
import { delete_img, download_img } from "@/constants";
import { formatDateOrTime, numberWithCommas } from "@/utils";
import { parseSelectedOptions, getAdjustmentTypeLabel } from "@/utils/report-utils";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
const StyledContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});
import { useReportDownload } from "@/hooks";

interface IColumnActions {
  action: (type: "download" | "delete", report: IReport) => void;
  actionsLoading: { delete: string[]; download: string[] };
}

export const reportColumns = ({
  action,
  actionsLoading,
}: IColumnActions): IColumn<IReport>[] => {
  const { downloadReport, downloadingReports } = useReportDownload();

  return [
    {
      name: "Date & Time",
      key: "createdAt",
      align: "left",
      width: "22%",
      minWidth: "160px",
      component: ({ createdAt }) => (
        <>
          <Typography variant="body2" color={"#030712"} sx={{ fontSize: { xs: '11px', md: '12px' } }}>
            {formatDateOrTime(createdAt ?? new Date(), "date")}
          </Typography>
          <Typography variant="body1" fontSize={"0.75rem"} color={"#8E9494"}>
            {formatDateOrTime(createdAt ?? new Date(), "time")}
          </Typography>
        </>
      ),
    },
    {
      name: "# of Entries",
      key: "entryCount",
      align: "center",
      width: "12%",
      minWidth: "90px",
      component: ({ entryCount }) => (
        <StyledContainer sx={{ '& *': { fontSize: { xs: '11px', md: '12px' } } }}>{numberWithCommas(entryCount || 0)}</StyledContainer>
      ),
    },
    {
      name: "Adjustment Type",
      key: "selectedOptions",
      align: "center",
      width: "22%",
      minWidth: "160px",
      component: ({ selectedOptions }) => {
        if (!selectedOptions) {
          return <StyledContainer>-</StyledContainer>;
        }

        try {
          const parsed = parseSelectedOptions(selectedOptions);
          const typeLabel = getAdjustmentTypeLabel(parsed.adjustmentType);

          return (
            <StyledContainer sx={{ flexDirection: 'column', gap: '2px', '& *': { fontSize: { xs: '11px', md: '12px' } } }}>
              <Tooltip title={typeLabel}>
                <Typography variant="body2" color="#030712" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {typeLabel}
                </Typography>
              </Tooltip>
              {parsed.adjustmentValue && (
                <Tooltip title={parsed.adjustmentValue}>
                  <Typography variant="caption" color="#8E9494" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {parsed.adjustmentValue}
                  </Typography>
                </Tooltip>
              )}
            </StyledContainer>
          );
        } catch (error) {
          return <StyledContainer>-</StyledContainer>;
        }
      },
    },
    {
      name: "Information",
      key: "_id",
      align: "center",
      width: "10%",
      minWidth: "80px",
      component: (report) => (
        <StyledContainer>
          <ReportsInfoTooltip report={report} />
        </StyledContainer>
      ),
    },
    {
      name: "Actions",
      key: "actions",
      align: "center",
      width: "20%",
      minWidth: "180px",
      component: (report) => {
        const { _id, isCompleted } = report;

        const isDownloading = downloadingReports.includes(_id);
        const isDeleting = actionsLoading["delete"].includes(_id);

        const handleDownload = async () => {
          try {
            await downloadReport(_id);
          } catch (error) {}
        };

        return (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              flexWrap: { xs: "wrap", md: "nowrap" },
              width: "100%",
            }}
          >
            <ContainedButton
              startIcon={
                isDownloading ? (
                  <CircularProgress size={18} sx={{ color: "#030712" }} />
                ) : (
                  <IconImage src={download_img} size="auto" />
                )
              }
              style={{
                borderColor: "#E6E7E7",
                color: "#030712",
                fontWeight: 500,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              variant="outlined"
              title={!isCompleted ? "Processing..." : "Download Report"}
              disabled={isDownloading || !isCompleted}
              onClick={handleDownload}
            />
            <ContainedButton
              startIcon={
                isDeleting ? (
                  <CircularProgress size={18} sx={{ color: "#EA4334" }} />
                ) : (
                  <IconImage src={delete_img} size="auto" />
                )
              }
              style={{
                color: "#EA4334",
                borderColor: "#E6E7E7",
                fontWeight: 500,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
              variant="outlined"
              title="Delete"
              disabled={isDeleting}
              onClick={() => action("delete", report)}
            />
          </Box>
        );
      },
    },
  ];
};
